import React from "react";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";

export type StudentParentRef = {
  id: string;
  firstName: string;
  lastName: string;
};

// A student already in the system, selected from search. Linked by its real id;
// never recreated.
export type ExistingStudentEntry = {
  kind: "existing";
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  parents: StudentParentRef[];
};

// A student being created with the parent. Carries a client-generated id so the
// relationship can reference it in the same createFamily call.
export type NewStudentEntry = {
  kind: "new";
  id: string;
  // Display-only: once confirmed via "Add student", the editable sub-form collapses
  // into a read-only chip. The entry is in the payload either way.
  confirmed: boolean;
  firstName: string;
  middleName: string | null;
  lastName: string;
  birthDate: string | null;
  referenceId: string | null;
  useParentAddress: boolean;
  streetAddress: string | null;
  streetAddressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
};

export type StudentEntry = ExistingStudentEntry | NewStudentEntry;

export function useStudentEntries() {
  const [entries, setEntries] = React.useState<StudentEntry[]>([]);

  const addExisting = React.useCallback((applicant: ApplicantDuplicate) => {
    setEntries((prev) => {
      if (prev.some((entry) => entry.id === applicant.id)) return prev;
      return [
        ...prev,
        {
          kind: "existing",
          id: applicant.id,
          firstName: applicant.first_name,
          lastName: applicant.last_name,
          birthDate: applicant.birth_date,
          parents: applicant.guardians.map((guardian) => ({
            id: guardian.id,
            firstName: guardian.first_name,
            lastName: guardian.last_name,
          })),
        },
      ];
    });
  }, []);

  // Add an empty, editable new student to the payload, optionally prefilled from
  // the typed search name. Edited in place via updateDraft; nothing is created
  // until the page's Create button submits.
  const addDraft = React.useCallback(
    (prefill?: { firstName?: string; lastName?: string }) => {
      setEntries((prev) => [
        ...prev,
        {
          kind: "new",
          id: crypto.randomUUID(),
          confirmed: false,
          firstName: prefill?.firstName ?? "",
          middleName: null,
          lastName: prefill?.lastName ?? "",
          birthDate: null,
          referenceId: null,
          useParentAddress: true,
          streetAddress: null,
          streetAddressLine2: null,
          city: null,
          state: null,
          zipCode: null,
        },
      ]);
    },
    []
  );

  const updateDraft = React.useCallback(
    (id: string, patch: Partial<NewStudentEntry>) => {
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === id && entry.kind === "new"
            ? { ...entry, ...patch }
            : entry
        )
      );
    },
    []
  );

  // Collapse a new draft into a read-only chip. The entry stays in the payload.
  const confirmDraft = React.useCallback((id: string) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id && entry.kind === "new"
          ? { ...entry, confirmed: true }
          : entry
      )
    );
  }, []);

  const remove = React.useCallback((id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  return { entries, addExisting, addDraft, updateDraft, confirmDraft, remove };
}
